import * as R from "ramda";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom-v5-compat";

import { AnswerContextValue, useAnswerContext } from "contexts/answerContext";
import { useVersionedClientRef } from "contexts/versionedClient";
import keys from "queries/keys";
import resourceOperations, { CreateOptions } from "queries/resourceOperations";
import { getFieldIdentsForPages } from "reducers/answerContexts";
import { getIsZoningEnabled } from "reducers/projects";
import {
  AnsweredEntity,
  Project,
  Requirement,
  RequirementSection,
  SectionList,
  ZonePermission,
  ZoningAnswer,
} from "types/entities";
import { compact } from "utils/func";
import { getPermission } from "utils/zoning";

import { toKey } from "./fields";

const ScopingSection = {
  name: "Scoping",
  key: "scoping",
  type: "RequirementSection",
} as RequirementSection;

const FeeScopingSection = {
  name: "Fee Scoping",
  key: "fee_scoping",
  type: "RequirementSection",
} as RequirementSection;

const getSectionByID = (
  requirement: Requirement,
  sectionID: number | string,
): RequirementSection => {
  switch (sectionID) {
    case "scoping": {
      return ScopingSection;
    }
    case "fee_scoping": {
      return FeeScopingSection;
    }
    default:
      return R.compose(
        R.find(R.propEq(sectionID, "id")),
        R.propOr([], "sections"),
      )(requirement) as RequirementSection;
  }
};

export const queryKeys = {
  projectScope: keys.projectScope,
  projects: keys.projects,
};

const { pathFor, useRead, useCreate, useUpdate } = resourceOperations({
  basePath: "/api/projects",
  baseKey: "projects",
  versioned: true,
});
export { pathFor };

export const useUpdateProject = useUpdate<Project>;
export const useProject = (id: number, opts = {}) =>
  useRead<Project>(id, { ...opts, disableVersionedAnnotation: true });
export const useProjects = (opts = {}) => useRead<Project[]>(null, opts);

export const useScopeProject = (id: number, { onSuccess = R.identity, ...opts } = {}) => {
  const queryClient = useQueryClient();
  const client = useVersionedClientRef();
  const { dispatch } = useAnswerContext();

  return useMutation({
    ...opts,
    mutationKey: keys.projectScope(id),
    async mutationFn() {
      const { data } = await client.current.put(pathFor([id, "scope"]), {});
      return data;
    },
    async onSuccess(updatedProject) {
      dispatch({ type: "updateRecord", record: updatedProject });
      await queryClient.invalidateQueries(keys.projects(id));
      onSuccess(updatedProject);
    },
  });
};

interface MutationArgs {
  fieldIDs: number[];
  contextuallyRequiredFieldIDs: number[];
}

export const useValidateProject = (projectID: number, opts) => {
  const client = useVersionedClientRef();
  return useMutation({
    async mutationFn({ fieldIDs, contextuallyRequiredFieldIDs }: MutationArgs) {
      const { data } = await client.current.get(pathFor([projectID, "validate"]), {
        params: {
          field_ids: fieldIDs,
          contextually_required_field_ids: contextuallyRequiredFieldIDs,
        },
      });
      return data;
    },
    ...opts,
  });
};

export const useCreateProject = ({ onSuccess, ...opts }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  opts.onSuccess = async ({ project }) => {
    await queryClient.refetchQueries("session");
    if (onSuccess) {
      onSuccess(project);
    } else {
      navigate(`/projects/${project.id}/guide/${getFirstPage(project)}`);
    }
  };

  return useCreate(null, opts);
};

export const useRenewProject = (id: number, opts: CreateOptions<any, any> = {}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  opts.onSuccess = async ({ project, requirement_slug }) => {
    await queryClient.refetchQueries("session");
    navigate(`/projects/${project.id}/direct/${requirement_slug}`);
  };

  return useCreate([id, "renew"], opts);
};

export const useDownloadProject = (id: number, opts = {}) => {
  const client = useVersionedClientRef();
  return useMutation({
    ...opts,
    mutationFn: async () => {
      const { data } = await client.current.get(pathFor([id, "pdf"].join(".")));
      return data;
    },
  });
};

export const usePushProjectToAccela = (id: number, opts = {}) => {
  const client = useVersionedClientRef();
  return useMutation({
    ...opts,
    mutationFn: async ({ requirement_id }: { requirement_id: number }) =>
      client.current.put(pathFor([id, "push_to_accela"]), {
        requirement_id,
      }),
  });
};

export const isAnswered = (a: any): boolean => {
  if (Array.isArray(a)) return !R.pipe(R.flatten, compact, R.isEmpty)(a);

  if (a instanceof Object) return !R.pipe(R.values, compact, R.flatten, R.isEmpty)(a);

  if (typeof a === "string" || a instanceof String) return !R.isEmpty(a);

  return !R.isNil(a);
};

export const getAnswer = (project: AnsweredEntity, key: string) =>
  R.path(["answers", key], project);

export const isFieldAnswered = R.curry((project, key) => isAnswered(getAnswer(project, key)));

export const getGuideFieldIdents = (context: AnsweredEntity): string[] => {
  const pages = R.pathOr({}, ["user_pages", "guide", "pages"], context);
  return getFieldIdentsForPages(...R.values(pages));
};

export const getUnansweredCountForFieldIdents = (project: AnsweredEntity, fieldIdents: string[]) =>
  R.count((ident) => !isFieldAnswered(project, toKey(ident)), fieldIdents);

export const getAnsweredGuideFieldIdents = (project: Project) => {
  const fieldIdents = getGuideFieldIdents(project);
  return R.filter((ident) => isFieldAnswered(project, toKey(ident)), fieldIdents);
};

export const getRequirementSections = (project: Project, requirementKey: string): SectionList =>
  R.pathOr({} as SectionList, ["user_pages", "requirements", requirementKey], project);

export const getRequirementSectionsList = (
  project: Project,
  requirement: Requirement,
  opts: { fieldFilter?: (field: any) => boolean; skipSections?: string[] } = {},
) => {
  const sections = getRequirementSections(project, requirement.key);
  const order = R.propOr([], "order", sections) as string[];
  let seenFields = [];
  return order.reduce((acc, sectionID) => {
    if (opts.skipSections && opts.skipSections.includes(sectionID)) {
      return acc;
    }
    const section = getSectionByID(requirement, sectionID);
    let fields = R.path(["sections", sectionID], sections);
    if (opts.fieldFilter) {
      fields = R.filter(opts.fieldFilter, fields);
    }
    if (fields && fields.length > 0) {
      acc.push(R.assoc("fieldIdents", R.without(seenFields, fields), section));
      seenFields = R.concat(seenFields, fields);
    }
    return acc;
  }, []) as RequirementSection[];
};

export const getScopingSections = (project: Project) => {
  const requirementKeys = R.keys(R.pathOr({}, ["user_pages", "requirements"], project));
  const requirementSections = R.chain((requirementKey) =>
    getRequirementSectionsList(project, { key: requirementKey } as Requirement),
  )(requirementKeys);

  const scopingSections = R.reduce(
    (acc, section) => {
      if (acc[section.key]) {
        return R.over(
          R.lensPath([section.key, "fieldIdents"]),
          R.pipe(R.concat(section.fieldIdents), R.uniq),
          acc,
        );
      }
      return R.assoc(section.key, section, acc);
    },
    {},
    requirementSections,
  ) as { [key: string]: RequirementSection };

  return R.reject(R.isNil)([scopingSections.scoping, scopingSections.fee_scoping]);
};

export const getRequirementSectionFields = (
  project: AnsweredEntity,
  requirementKey: string,
  section: string,
) => R.pathOr([], ["user_pages", "requirements", requirementKey, "sections", section], project);

export const getFieldIdentsForGuidePage = (context: AnsweredEntity, pageKey: string) =>
  R.pathOr([], ["user_pages", "guide", "pages", pageKey], context);

export const getUnansweredFieldsCount = (context: AnsweredEntity, idents: string[]) =>
  R.filter((ident) => !isFieldAnswered(context, toKey(ident)), idents).length;

export const getFirstPage = R.path(["user_pages", "guide", "order", 0]);

interface ZoningData {
  category: string;
  isZoningEnabled: boolean;
  permission: ZonePermission;
  zoning: ZoningAnswer;
}
export const getZoningData = (answerContext: AnswerContextValue): ZoningData => {
  const { record } = answerContext;
  const zoning = getAnswer(record, "zoning");
  const isZoningEnabled = getIsZoningEnabled(record);
  const permission = getPermission(zoning);
  const category = R.prop("category", permission || {});

  return {
    category,
    isZoningEnabled,
    permission,
    zoning,
  };
};
